import { useCallback, useEffect, useRef, useState } from "react";
import { AiImprovementJobStatusDto, GfImage } from "@swagger/typescript-fetch-goflyer";
import { useAuthHeaders } from "./useAuthHeaders";
import {
  fetchAiImprovementJobStatusSafe,
  isAiImprovementTerminal,
  parseAiImprovementProgress,
} from "../utils/aiImageImprovement";

const MAX_EMPTY_POLLS = 30;

export function useAiImprovementJob(jobId: string | null, intervalMs = 2500) {
  const authHeaders = useAuthHeaders();
  const authToken = authHeaders.headers?.Authorization ?? "";
  const [status, setStatus] = useState<AiImprovementJobStatusDto | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [jobNotFound, setJobNotFound] = useState(false);
  const jobIdRef = useRef(jobId);
  jobIdRef.current = jobId;

  const poll = useCallback(async (): Promise<
    AiImprovementJobStatusDto | "error" | "not_found" | null
  > => {
    const activeJobId = jobIdRef.current;
    if (!activeJobId) return null;

    const result = await fetchAiImprovementJobStatusSafe(activeJobId, {
      headers: { Authorization: authToken },
    });

    if (jobIdRef.current !== activeJobId) return null;

    if (result.type === "success") {
      setStatus(result.job);
      setError(null);
      setJobNotFound(false);
      return result.job;
    }
    if (result.type === "not_found") {
      setJobNotFound(true);
      setError(null);
      return "not_found";
    }
    setError(
      result.cause instanceof Error
        ? result.cause
        : new Error("AI improvement status request failed"),
    );
    setJobNotFound(false);
    return "error";
  }, [authToken]);

  useEffect(() => {
    if (!jobId) {
      setStatus(null);
      setError(null);
      setJobNotFound(false);
      return;
    }

    setJobNotFound(false);

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let emptyPollCount = 0;

    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const scheduleNext = () => {
      clearTimer();
      if (cancelled || document.hidden) return;
      timer = setTimeout(tick, intervalMs);
    };

    const tick = async () => {
      if (cancelled) return;
      const result = await poll();
      if (cancelled) return;

      if (result === "error" || result === "not_found") {
        return;
      }

      if (!result) {
        emptyPollCount += 1;
        if (emptyPollCount < MAX_EMPTY_POLLS && jobIdRef.current) {
          scheduleNext();
        }
        return;
      }

      emptyPollCount = 0;
      if (!isAiImprovementTerminal(result.state)) {
        scheduleNext();
      }
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        clearTimer();
        return;
      }
      if (jobIdRef.current) {
        tick();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    tick();

    return () => {
      cancelled = true;
      clearTimer();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [jobId, intervalMs, poll]);

  const progress = parseAiImprovementProgress(status?.progress);
  const isDone = status ? isAiImprovementTerminal(status.state) : false;

  return {
    status,
    error,
    jobNotFound,
    isDone,
    isSuccess: status?.state === AiImprovementJobStatusDto.StateEnum.Completed,
    isFailed: status?.state === AiImprovementJobStatusDto.StateEnum.Failed,
    progress,
    newImage: status?.result?.newImage as GfImage | undefined,
  };
}
